import { isArray, unique } from './array';
import { isBoolean } from './boolean';
import { isDate, isDateTime } from './date';
import { isNil, isNotNull, isUndefined, TMaybe } from './maybe';
import { isNumber } from './number';
import { anonymize, isEmptyString, isString } from './string';

import type { TAnyObject } from './any';

type TWithoutObject<TPType> = TPType extends object ? never : TPType;
type TJustObject<TPType> = TPType extends object ? TPType : never;

function isObject<TPObject extends Record<string, any>>(value: unknown): value is TPObject {
  return isNotNull(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasProperty<TPObject extends TAnyObject, TPKey extends keyof TPObject>(
  object: TPObject,
  key: TPKey
): object is TPObject & Record<TPKey, TPObject[TPKey]> {
  return Object.prototype.hasOwnProperty.call(object, key);
}

/**
 * Recursively merges two objects or arrays. If both `target` and `source`
 * are arrays, the arrays are concatenated. If both are objects, the properties
 * are merged recursively. If a property exists in both objects and is not an
 * array or object, the value from `source` will overwrite the value in `target`.
 *
 * @param target - The target object or array to merge into.
 * @param source - The source object or array to merge from.
 * @param isProp - A flag indicating whether the merge is for a property of an object. Defaults to false.
 * @returns A merged object or array.
 */

function deepMerge<TPTarget = any, TPSource = any>(
  target: TPTarget,
  source: TPSource,
  isProp: boolean = false
): TPTarget & TPSource {
  // If target is undefined, return source.
  if (isUndefined(target)) {
    return source as TPTarget & TPSource;
  }

  // If source is undefined, return target.
  else if (isUndefined(source)) {
    return target as TPTarget & TPSource;
  }

  // If source is Date, return source.
  else if (isDate(source) || isDateTime(source)) {
    return source as TPTarget & TPSource;
  }

  // If both target and source are arrays, return the concatenation of both.
  else if (isArray(target) && isArray(source)) {
    if (isProp) {
      return source as TPTarget & TPSource;
    }

    if (target.length === 0) {
      return source as TPTarget & TPSource;
    }

    if (source.length === 0) {
      return target as TPTarget & TPSource;
    }

    return unique(target.concat(source)) as TPTarget & TPSource;
  }

  // If both target and source are objects, return the merged object.
  else if (isObject(target) && isObject(source)) {
    const result: Record<string, any> = {};

    for (const key in target) {
      if (hasProperty(target, key)) {
        if (isUndefined(result[key])) {
          result[key] = target[key];
        } else {
          result[key] = deepMerge(result[key], target[key], true);
        }
      }
    }

    for (const key in source) {
      if (hasProperty(source, key)) {
        if (isUndefined(result[key])) {
          result[key] = source[key];
        } else {
          result[key] = deepMerge(result[key], source[key], true);
        }
      }
    }

    return result as TPTarget & TPSource;
  }

  // Otherwise, return source.
  else {
    return source as TPTarget & TPSource;
  }
}

/**
 * Recursively cleans the given value, removing any null or undefined values, empty strings,
 * empty arrays, and empty objects.
 *
 * @param value The value to clean.
 * @return The cleaned value, or undefined if the cleaned value is empty.
 */
function deepClean(
  value: Record<string, any> | undefined,
  removeNull: boolean = true
): Record<string, any> | undefined {
  if (isUndefined(value)) {
    return;
  }

  const clean = (value: any): any => {
    // If the value is an object, recursively clean its values, remove any undefined values or empty objects,
    // and return undefined if the object is empty, or the cleaned object otherwise.

    // If the value is a string, trim it and return undefined if it's empty, or the trimmed value otherwise.
    if (isString(value)) {
      const trimmed = value.trim();

      if (isEmptyString(trimmed)) {
        return;
      } else {
        return trimmed;
      }
    }

    // If the value is a number or a boolean, return it as is.
    else if (isNumber(value) || isBoolean(value) || isDate(value) || isDateTime(value)) {
      return value;
    }

    // If the value is null or undefined
    else if (isNil(value)) {
      if (removeNull) {
        return;
      } else {
        if (value === null) {
          return null;
        } else {
          return;
        }
      }
    }

    // If the value is an array, recursively clean its values, remove any undefined values or empty arrays
    // and return undefined if the array is empty, or the cleaned array otherwise.
    else if (isArray(value)) {
      const cleaned = [];

      for (let i = 0; i < value.length; i++) {
        const cleanedValue = clean(value[i]);

        if (!isUndefined(cleanedValue)) {
          cleaned.push(cleanedValue);
        }
      }

      if (cleaned.length === 0) {
        return;
      } else {
        return cleaned;
      }
    }

    // If the value is an object, recursively clean its values, remove any undefined values or empty objects,
    // and return undefined if the object is empty, or the cleaned object otherwise.
    else if (isObject(value)) {
      const cleaned: Record<string, any> = {};

      for (const key in value) {
        // Make sure to only clean own properties, not inherited ones.
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          const cleanedValue = clean(value[key]);

          if (!isUndefined(cleanedValue)) {
            cleaned[key] = cleanedValue;
          }
        }
      }

      if (Object.keys(cleaned).length === 0) {
        return;
      } else {
        return cleaned;
      }
    }

    // If the value is not a string, number, boolean, array, or object, it cannot be cleaned, so return it as is.
    else {
      return value;
    }
  };

  return clean(value);
}

/**
 * Recursively anonymizes sensitive data in an object or string.
 *
 * @param value The value to anonymize.
 * @param props An optional array of property names to anonymize.
 * @returns The anonymized value.
 */
function deepAnonymize(value: any, props: string[] = ['password', 'hash', 'token']): any {
  // If the value is a string, anonymize it and return it.
  if (isString(value)) {
    return anonymize(value);
  }

  // If the value is an array, recursively anonymize its values.
  else if (isArray(value)) {
    return value.map((item) => deepAnonymize(item, props));
  }

  // If the value is an object, recursively anonymize any sensitive properties.
  else if (isObject(value)) {
    // Create a new object to hold the anonymized values.
    const anonymized: Record<string, any> = {};

    for (const key in value) {
      if (hasProperty(value, key)) {
        // Check if the object has the property.
        if (props.includes(key)) {
          // If the property is sensitive, anonymize it.
          anonymized[key] = deepAnonymize(value[key], props);
        } else if (isObject(value[key])) {
          // If the property is an object, recursively anonymize it.
          anonymized[key] = deepAnonymize(value[key], props);
        } else {
          // Otherwise, copy the property value to the anonymized object.
          anonymized[key] = value[key];
        }
      }
    }
    return anonymized;
  }

  // If the value is not an object or string, return it as-is.
  else {
    return value;
  }
}

/**
 * Gets the value at the given path in an object.
 *
 * @param object The object to get the value from.
 * @param path The path to the value, e.g. "a.b.c" or "a.b[0]".
 * @returns The value at the given path, or undefined if it does not exist.
 */
function get<TPValue = any>(object: unknown, path: string): TPValue | undefined {
  if (!isObject(object)) {
    return;
  }

  // Split the path by dot and handle array indices
  const pathArr: Array<string | number> = path.split('.').flatMap((part) => {
    const match = part.match(/([^[\]]+)|(\[\d+\])/g);
    return match ? match.map((key) => (key.startsWith('[') ? +key.slice(1, -1) : key)) : part;
  });

  let result: any = object;

  // Traverse the object using the current key
  for (const key of pathArr) {
    result = result[key];

    // If the result is undefined, the path does not exist, so return undefined
    if (isUndefined(result)) {
      return;
    }
  }

  // If the path exists, return the final result
  return result as TPValue | undefined;
}

/**
 * Sets the value at the given path in an object.
 * If the path does not exist, it will be created as nested objects.
 *
 * @param object The object to set the value in.
 * @param path The path to the value, e.g. "a.b.c".
 * @param value The value to set.
 */
function set<TPValue = any>(object: unknown, path: string, value: TPValue): void {
  if (!isObject(object)) {
    return;
  }

  const pathArr: string[] = path.split('.');
  let result = object;

  // Traverse the path, creating nested objects as necessary
  for (let i = 0; i < pathArr.length - 1; i++) {
    const key: string = pathArr[i];
    const nextResult = result[key];

    // If the next result is undefined, create a nested object or array depending on the next key's type
    if (isUndefined(nextResult)) {
      const nextKey: string = pathArr[i + 1];
      result[key] = typeof nextKey === 'number' ? [] : {};
    }

    result = result[key];
  }

  // Set the value at the final key in the path
  result[pathArr[pathArr.length - 1]] = value;
}

function unset(object: Record<string, any>, path: string): void {
  const pathArr: string[] = path.split('.');
  let result: any = object;

  // Traverse the path, creating nested objects as necessary
  for (let i = 0; i < pathArr.length - 1; i++) {
    const key: string = pathArr[i];
    const nextResult = result[key];

    // If the next result is undefined, create a nested object or array depending on the next key's type
    if (isUndefined(nextResult)) {
      const nextKey: string = pathArr[i + 1];
      result[key] = typeof nextKey === 'number' ? [] : {};
    }

    result = result[key];
  }

  // Delete the value at the final key in the path
  delete result[pathArr[pathArr.length - 1]];
}

function isPath(path: string): boolean {
  return /^[a-zA-Z0-9_.-]+$/.test(path);
}

function getJustObject<TPType>(
  value: TPType,
  orElse: (value: TWithoutObject<TPType>) => TMaybe<TJustObject<TPType>>
): TMaybe<TJustObject<TPType>>;
function getJustObject<TPType>(
  value: TPType,
  orElse: (value: TWithoutObject<TPType>) => Promise<TMaybe<TJustObject<TPType>>>
): Promise<TMaybe<TJustObject<TPType>>>;
function getJustObject<TPType>(
  value: TPType,
  orElse: (value: TWithoutObject<TPType>) => TMaybe<TJustObject<TPType>>
): TMaybe<TJustObject<TPType>> {
  return isObject(value) ? (value as TJustObject<TPType>) : orElse(value as TWithoutObject<TPType>);
}

type TPlainObject = Record<string, any>;

function clone<TPValue = unknown>(source: TPValue): TPValue {
  if (isObject(source)) {
    const result: TPlainObject = {};
    for (const key in source as TPlainObject) {
      if (hasProperty(source as TPlainObject, key)) {
        result[key] = clone((source as any)[key]);
      }
    }
    return result as TPValue;
  } else if (isArray(source)) {
    return (source as any[]).map((item) => clone(item)) as unknown as TPValue;
  } else if (source instanceof Date) {
    return new Date((source as Date).getTime()) as unknown as TPValue;
  } else if (source instanceof RegExp) {
    const s = source as RegExp;
    return new RegExp(s.source, s.flags) as unknown as TPValue;
  }

  return source;
}

export {
  TWithoutObject,
  TJustObject,
  isObject,
  hasProperty,
  deepMerge,
  deepClean,
  deepAnonymize,
  clone,
  get,
  set,
  unset,
  isPath,
  TAnyObject,
  getJustObject,
};
